import React from 'react';
import { Checkbox, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DetachIcon } from '@shared/components/icons/svg-icon.tsx';
import {
  DEVICE_STATUS_TONES,
  getDeviceStatus
} from '@modules/digital-signage/utils/digital-signage.utils.ts';
import { GroupDeviceRowProps } from '@modules/digital-signage/model/group-settings.model.ts';
import StatusLozenge from '@modules/digital-signage/components/group-settings/components/status-lozenge/status-lozenge.tsx';

const GroupDeviceRow: React.FC<GroupDeviceRowProps> = ({
  option,
  device,
  checked,
  disabled,
  onToggle,
  onRemove
}) => {
  const { t } = useTranslation();
  const deviceId = device?.deviceId;
  const status = getDeviceStatus(device);

  return (
    <div
      onClick={() => !disabled && onToggle(option.id)}
      className={`flex items-center gap-2.5 h-12 pl-3 pr-1.5 border-b last:border-b-0 border-(--gray-9) transition-colors ${
        disabled
          ? 'bg-(--white) opacity-60 cursor-default'
          : checked
            ? 'bg-(--primary-50) cursor-pointer'
            : 'bg-(--white) hover:bg-(--gray-11) cursor-pointer'
      }`}
    >
      <Checkbox
        className="primary-checkbox"
        checked={checked}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onToggle(option.id)}
      />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <Tooltip title={option.title} placement="top">
          <Typography
            variant="body2"
            noWrap
            className="text-[13px]! font-semibold! text-(--dark-1)!"
          >
            {option.title}
          </Typography>
        </Tooltip>
        {deviceId && (
          <Tooltip
            title={t('digital_signage_group_device_id', { id: deviceId })}
            placement="top"
          >
            <Typography
              variant="caption"
              noWrap
              className="text-[11px]! text-(--gray-2)!"
            >
              {t('digital_signage_group_device_id', { id: deviceId })}
            </Typography>
          </Tooltip>
        )}
      </div>
      <StatusLozenge
        tone={DEVICE_STATUS_TONES[status]}
        label={t(`digital_signage_group_status_${status}`)}
      />
      <Tooltip
        title={t('digital_signage_group_remove_from_group')}
        placement="top"
      >
        {/* span: a Tooltip needs an enabled child for events */}
        <span className="shrink-0">
          <IconButton
            aria-label={t('digital_signage_group_remove_from_group')}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(option.id);
            }}
            className="w-7.5! h-7.5! rounded-lg! text-(--blue-4)! hover:bg-(--red-4)/10! hover:text-(--red-4)!"
          >
            <DetachIcon className="w-3.75! h-3.75!" />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

export default GroupDeviceRow;
